// PriceFeed WebSocket Hook - Provides centralized WebSocket management for price-feed-api
// This connects to the real-time gold and silver price feed gateway

import { createContext, useContext, useEffect, useState } from 'react';
import io from 'socket.io-client';
import PriceFeedDataManager from '../services/PriceFeedDataManager';

const PriceFeedWebSocketContext = createContext();

export const PriceFeedWebSocketProvider = ({ children, socketUrl, apiKey }) => {
  const [socket, setSocket] = useState(null);
  const [dataManager, setDataManager] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [initializationError, setInitializationError] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    if (!socketUrl || !apiKey) {
      console.warn(
        'PriceFeed WebSocket URL or API key not provided, skipping connection',
      );
      setConnectionStatus('disabled');
      return;
    }

    let newSocket = null;

    try {
      console.log('Connecting to PriceFeed WebSocket:', socketUrl);

      // Create the WebSocket URL with API key as query parameter
      const wsUrlWithApiKey = `${socketUrl}?apikey=${apiKey}`;

      console.log('PriceFeed WebSocket URL with API key:', wsUrlWithApiKey);

      // Initialize socket connection - directly connect to the namespaced endpoint
      newSocket = io(wsUrlWithApiKey, {
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 20000,
      });

      // Connection event handlers
      newSocket.on('connect', () => {
        console.log('PriceFeed WebSocket connected successfully');
        setConnectionStatus('connected');
        setInitializationError(null);
      });

      newSocket.on('disconnect', (reason) => {
        console.log('PriceFeed WebSocket disconnected:', reason);
        setConnectionStatus('disconnected');
        setIsSubscribed(false);
      });

      newSocket.on('connect_error', (error) => {
        console.error('PriceFeed WebSocket connection error:', error);
        setConnectionStatus('error');
        setInitializationError(error.message || 'Connection failed');
      });

      newSocket.on('reconnect_attempt', (attemptNumber) => {
        console.log(
          `PriceFeed WebSocket reconnection attempt ${attemptNumber}`,
        );
        setConnectionStatus('reconnecting');
      });

      newSocket.on('reconnect_failed', () => {
        console.error(
          'PriceFeed WebSocket reconnection failed after all attempts',
        );
        setConnectionStatus('failed');
      });

      newSocket.on('reconnect', () => {
        console.log('PriceFeed WebSocket reconnected successfully');
        setConnectionStatus('connected');
        // Re-subscribe if we were subscribed before
        if (isSubscribed) {
          setTimeout(() => {
            if (dataManager) {
              dataManager.subscribeToCurrentPrice();
            }
          }, 1000);
        }
      });

      // Initialize data manager
      const manager = new PriceFeedDataManager(newSocket);

      setSocket(newSocket);
      setDataManager(manager);

      // Cleanup on unmount
      return () => {
        try {
          if (newSocket && newSocket.connected) {
            // Unsubscribe before disconnecting
            if (isSubscribed) {
              newSocket.emit('unsubscribe', {});
            }
            newSocket.removeAllListeners();
            newSocket.disconnect();
          }
        } catch (error) {
          console.error('Error during PriceFeed WebSocket cleanup:', error);
        }
      };
    } catch (error) {
      console.error('Failed to initialize PriceFeed WebSocket:', error);
      setConnectionStatus('failed');
      setInitializationError(error.message || 'Initialization failed');

      // Cleanup if socket was partially created
      if (newSocket) {
        try {
          newSocket.disconnect();
        } catch (cleanupError) {
          console.error(
            'Error during failed PriceFeed socket cleanup:',
            cleanupError,
          );
        }
      }
    }
  }, [socketUrl, apiKey]);

  // Auto-subscribe when connected
  useEffect(() => {
    if (dataManager && connectionStatus === 'connected' && !isSubscribed) {
      console.log('PriceFeed: Auto-subscribing to current price updates');
      dataManager.subscribeToCurrentPrice(['CTG']);
      setIsSubscribed(true);
    }
  }, [dataManager, connectionStatus, isSubscribed]);

  const value = {
    socket,
    dataManager,
    connectionStatus,
    isConnected: connectionStatus === 'connected',
    isConnecting:
      connectionStatus === 'connecting' || connectionStatus === 'reconnecting',
    hasError: connectionStatus === 'error' || connectionStatus === 'failed',
    errorMessage: initializationError,
    isSubscribed,
    // Utility methods
    subscribe: (companyCodes) => {
      if (dataManager && connectionStatus === 'connected') {
        dataManager.subscribeToCurrentPrice(companyCodes);
        setIsSubscribed(true);
      }
    },
    unsubscribe: () => {
      if (dataManager && connectionStatus === 'connected') {
        dataManager.unsubscribeFromCurrentPrice();
        setIsSubscribed(false);
      }
    },
  };

  return (
    <PriceFeedWebSocketContext.Provider value={value}>
      {children}
    </PriceFeedWebSocketContext.Provider>
  );
};

export const usePriceFeedWebSocket = () => {
  const context = useContext(PriceFeedWebSocketContext);
  if (!context) {
    throw new Error(
      'usePriceFeedWebSocket must be used within a PriceFeedWebSocketProvider',
    );
  }
  return context;
};

export default { PriceFeedWebSocketProvider, usePriceFeedWebSocket };
